const STORAGE_KEY = 'devtools-tool-usage-v1';
const MAX_TRACKED_TOOLS = 30;

export interface ToolUsage {
  count: number;
  lastUsedAt: number;
}

type ToolUsageMap = Record<string, ToolUsage>;

const readUsage = (): ToolUsageMap => {
  if (typeof window === 'undefined') return {};

  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

    return Object.fromEntries(
      Object.entries(value).filter(
        ([, usage]) =>
          usage &&
          typeof usage === 'object' &&
          typeof (usage as ToolUsage).count === 'number' &&
          typeof (usage as ToolUsage).lastUsedAt === 'number',
      ),
    ) as ToolUsageMap;
  } catch {
    return {};
  }
};

export const recordToolUse = (toolId: string, now = Date.now()) => {
  const usage = readUsage();
  const current = usage[toolId];

  usage[toolId] = {
    count: (current?.count ?? 0) + 1,
    lastUsedAt: now,
  };

  const trimmedUsage = Object.fromEntries(
    Object.entries(usage)
      .sort(([, left], [, right]) => right.lastUsedAt - left.lastUsedAt)
      .slice(0, MAX_TRACKED_TOOLS),
  );

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedUsage));
  } catch {
    // Personalization is optional; tools must still work when storage is unavailable.
  }
};

export const getFrequentToolIds = (limit = 6) =>
  Object.entries(readUsage())
    .sort(([, left], [, right]) => right.count - left.count || right.lastUsedAt - left.lastUsedAt)
    .slice(0, limit)
    .map(([toolId]) => toolId);
